import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'

/**
 * Spark Vaults V2 intrinsic APR — read straight off `vsr()`.
 *
 * Every `sp*` vault (`spUSDC`, `spUSDT`, `spETH`, `spPYUSD`, `spUSDG`)
 * is the same `SparkVault` implementation: a Sky-style rate accumulator
 * where `chi()` is the share price, `rho()` the last drip, and **`vsr()`
 * the Vault Savings Rate** — a per-second `ray` (1e27) exactly like
 * Sky's `ssr`/`dsr`. The APR is therefore a pure function of one call:
 *
 *   apr = (vsr / 1e27) ^ 31_536_000 − 1
 *
 * ## Why not `chi()` over an archival window
 *
 * The previous implementation recovered spUSDG's APR from the growth of
 * `chi()` across a 400k-block lookback, on the premise that the
 * instantaneous rate was not readable. It is — the getter is `vsr()`,
 * not the `ssr()` that had been probed. Reading it directly drops the
 * archival dependency entirely (Robinhood's public RPC prunes state past
 * ~2M blocks, so that window was one governance change away from
 * silently reporting a blended rate and one node upgrade away from
 * reporting nothing at all). Verified against the live deployments: the
 * `vsr` APR reproduces Sky's published SSR on the USD vaults to four
 * decimal places.
 *
 * ## Rate provenance
 *
 * `vsr` is pushed by `setVsr` (SETTER_ROLE) and bounded by
 * `minVsr`/`maxVsr`. It is an **administered** rate, not one derived
 * from realised earnings — the same trust assumption as the share price
 * itself, which is why `totalAssets()` is `convertToAssets(totalSupply)`
 * (a liability) rather than an asset-backed measure.
 *
 * Spark Savings **V1** (`sUSDC`) is a different product and is not
 * handled here: it holds sUSDS and swaps through PSM3, so its yield is
 * the Sky Savings Rate verbatim and it binds to `susdsFetcher`.
 *
 * Docs: https://docs.spark.fi/integrators/spark-vaults-v2
 */

/** 1e27 — `vsr()` and `chi()` are Maker/Sky `ray`s. */
const RAY = 10n ** 27n

/** Seconds per (non-leap) year — the SSR/DSR annualisation convention. */
const YEAR_SECONDS = 31_536_000

interface SparkVaultV2 {
  chainId: string
  address: Address
  symbol: string
  /**
   * Canonical 1delta token-list `assetGroup`, emitted as a second key so
   * the **lending** join (`intrinsicYields[asset.assetGroup]`) resolves —
   * these tokens are used as collateral (the Morpho sUSDC/USDC market on
   * Optimism, for one), and without it a market's `intrinsicYield` reads
   * 0. Same dual-key convention as [frax.ts](./frax.ts).
   *
   * Copied character-for-character from the live list; the strings are
   * `Name::SYMBOL` and are NOT self-consistent about casing, so they must
   * not be "tidied". Absent where the token is not in the list yet —
   * spPYUSD, spUSDG, and the Avalanche/Arbitrum/X-Layer `sp*`
   * deployments still resolve through the savings registry's `yieldKey`,
   * they just have no lending-side join to satisfy.
   */
  assetGroup?: string
}

/**
 * Every live Spark Vaults V2 deployment, from
 * `sparkdotfi/spark-address-registry` (`SPARK_VAULT_V2_*`). Addresses are
 * CREATE2-deterministic per asset, so `spUSDC` carries the same address
 * on Ethereum and Avalanche while remaining two independent vaults with
 * independent `vsr`s.
 */
export const SPARK_VAULTS_V2: SparkVaultV2[] = [
  {
    chainId: Chain.ETHEREUM_MAINNET,
    address: '0x28b3a8fb53b741a8fd78c0fb9a6b2393d896a43d',
    symbol: 'spUSDC',
    assetGroup: 'Spark Savings USDC::SPUSDC',
  },
  {
    chainId: Chain.ETHEREUM_MAINNET,
    address: '0xe2e7a17dff93280dec073c995595155283e3c372',
    symbol: 'spUSDT',
    assetGroup: 'Spark Savings USDT::SPUSDT',
  },
  {
    chainId: Chain.ETHEREUM_MAINNET,
    address: '0xfe6eb3b609a7c8352a241f7f3a21cea4e9209b8f',
    symbol: 'spETH',
    // NB: three unrelated tokens share the `SPETH` ticker in the list
    // (Spectrum ETH, Kroma Staked ETH) — only the full group is safe.
    assetGroup: 'Spark Savings ETH::SPETH',
  },
  {
    chainId: Chain.ETHEREUM_MAINNET,
    address: '0x80128dbb9f07b93dde62a6daeadb69ed14a7d354',
    symbol: 'spPYUSD',
  },
  {
    chainId: Chain.AVALANCHE_C_CHAIN,
    address: '0x28b3a8fb53b741a8fd78c0fb9a6b2393d896a43d',
    symbol: 'spUSDC',
  },
  {
    chainId: Chain.ARBITRUM_ONE,
    address: '0x45d91340b3b7b96985a72b5c678f7d9e8d664b62',
    symbol: 'spUSDT',
  },
  {
    chainId: Chain.X_LAYER_MAINNET,
    address: '0xc358c90d32375721cb3924320fdc2f8b694347ca',
    symbol: 'spUSDT',
  },
  {
    chainId: Chain.ROBINHOOD_CHAIN,
    address: '0xde770c84fe66e063336b31737cfe9790f18c4087',
    symbol: 'spUSDG',
  },
]

/**
 * Result key for a V2 vault. Chain-scoped because the same symbol is
 * deployed on several chains as **independent** vaults — `spUSDC` on
 * Ethereum and Avalanche happen to run the same rate today, but nothing
 * ties them together and `setVsr` is per-deployment.
 */
export const sparkVaultV2Key = (chainId: string, symbol: string): string =>
  `SPARK_V2::${chainId}::${symbol}`

/** Robinhood spUSDG — the first V2 vault we carried, kept as a named
 *  export because the registry and its test reference it directly. */
export const SPARK_SAVINGS_USDG_KEY = sparkVaultV2Key(
  Chain.ROBINHOOD_CHAIN,
  'spUSDG',
)

/** Savings **V1** `sUSDC` — one key for all five chains, because they
 *  share one rate (see {@link SKY_SSR_SOURCE}). */
export const SPARK_SUSDC_KEY = 'SPARK_V1::sUSDC'

/**
 * `sUSDC`'s canonical token-list `assetGroup`.
 *
 * The list carried this asset as TWO groups — `Spark USDC::SUSDC` for
 * the Ethereum deployment and `Spark USDC Vault::sUSDC` for the L2s —
 * even though `name()` reads "Spark USDC Vault" on every one of them.
 * `assetGroupUnifier.ts` now aliases the former into the latter, so this
 * is the single group the lending join sees. The pre-alias string is
 * emitted too, so a stale published list still resolves.
 */
export const SPARK_SUSDC_GROUP_KEY = 'Spark USDC Vault::sUSDC'
const SPARK_SUSDC_LEGACY_GROUP_KEY = 'Spark USDC::SUSDC'

/**
 * Mainnet `sUSDS`, read for its `ssr()`.
 *
 * A `sUSDC` share **is** an sUSDS — the vault holds sUSDS and prices
 * both legs through PSM3 — so its yield is the Sky Savings Rate exactly,
 * and identically on all five chains (verified: the share price reads
 * 1.104891 on Ethereum, Base, Arbitrum, Optimism and Unichain
 * simultaneously). Reading `ssr()` here rather than reusing
 * `susdsFetcher` keeps every Spark key owned by one fetcher and takes
 * $375M of sUSDC off an external API — the two agree to six decimals,
 * since `apyToApr` of the compounded APY is by construction the same
 * `(rate − 1) × SECONDS_PER_YEAR`.
 */
const SKY_SSR_SOURCE: Address = '0xa3931d71877c0e7a3148cb7eb4463524fec27fbd'

const VSR_ABI = [
  {
    name: 'vsr',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

const SSR_ABI = [
  {
    name: 'ssr',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * Annualise a per-second `ray` rate into a percentage **APR**.
 *
 * `supplyRate` is a nominal APR everywhere in this package — Sky's own
 * entries run their APY through
 * [`apyToApr`](../../../utils/parsing.ts), which for a per-second rate
 * `r` reduces exactly to `(r − 1) × SECONDS_PER_YEAR`. So the honest
 * conversion here is the simple product, not `r^SECONDS_PER_YEAR − 1`:
 * the latter is the APY and would report spUSDC at 3.52 % while sUSDC —
 * the same Sky rate, one product over — reported 3.459 %.
 *
 * The excess over 1 is taken in integer space first (`vsr - RAY`) and
 * only then converted to a float. Going through `Number(vsr) / 1e27`
 * instead would spend all 15–16 significant digits of the double on the
 * leading `1.000000001…`, leaving barely six on the part that actually
 * carries the rate.
 */
export const aprFromVsr = (vsr: bigint): number => {
  if (vsr <= RAY) return 0
  const perSecond = Number(vsr - RAY) / 1e27
  return perSecond * YEAR_SECONDS * 100
}

/** Chains covered, in roster order, deduped. */
const chainsOf = (vaults: SparkVaultV2[]): string[] => [
  ...new Set(vaults.map((v) => v.chainId)),
]

/**
 * Reads `vsr()` for every vault on one chain in a single multicall.
 *
 * NB: through `multicallRetryUniversal`, never a bare `getEvmClient` —
 * see the note in [frax.ts](./frax.ts); several leading mainnet
 * endpoints reject `eth_call` outright and a fetcher pinned to one of
 * them silently reports 0 %.
 */
const fetchChain = async (
  chainId: string,
  vaults: SparkVaultV2[],
): Promise<Record<string, number>> => {
  const results = (await multicallRetryUniversal({
    chain: chainId,
    abi: VSR_ABI,
    calls: vaults.map((v) => ({
      address: v.address,
      name: 'vsr',
      params: [],
    })),
    allowFailure: false,
  })) as bigint[]

  const out: Record<string, number> = {}
  vaults.forEach((v, i) => {
    const vsr = results[i]
    if (typeof vsr !== 'bigint' || vsr < RAY) return
    const apr = aprFromVsr(vsr)
    out[sparkVaultV2Key(v.chainId, v.symbol)] = apr
    // Second key for the lending-market join — see `SparkVaultV2.assetGroup`.
    if (v.assetGroup) out[v.assetGroup] = apr
  })
  return out
}

/** Savings V1 `sUSDC` — the Sky Savings Rate, one rate for five chains. */
const fetchSusdcApr = async (): Promise<Record<string, number>> => {
  const [ssr] = (await multicallRetryUniversal({
    chain: Chain.ETHEREUM_MAINNET,
    abi: SSR_ABI,
    calls: [{ address: SKY_SSR_SOURCE, name: 'ssr', params: [] }],
    allowFailure: false,
  })) as bigint[]

  if (typeof ssr !== 'bigint' || ssr < RAY) {
    throw new Error('Spark sUSDC: unreadable ssr()')
  }
  // `ssr` is the same per-second ray shape as `vsr`.
  const apr = aprFromVsr(ssr)
  return {
    [SPARK_SUSDC_KEY]: apr,
    [SPARK_SUSDC_GROUP_KEY]: apr,
    [SPARK_SUSDC_LEGACY_GROUP_KEY]: apr,
  }
}

/**
 * Every Spark savings rate — both products, all chains, one fetcher.
 *
 * Owning V1 and V2 together is deliberate: the savings registry dedups
 * by fetcher ref, so one pass covers all 13 rows, and it keeps the
 * `assetGroup` aliases (which the lending join needs) in the same place
 * as the keys the registry binds.
 */
export const sparkSavingsFetcher: YieldFetcher = {
  label: 'SPARK_SAVINGS',
  fetch: async () => {
    // Per-source isolation: a dead X Layer RPC must not zero out the
    // ~$700M of Ethereum vaults, and `safeFetch` upstream would drop the
    // whole fetcher on a single throw.
    const settled = await Promise.allSettled([
      ...chainsOf(SPARK_VAULTS_V2).map((chainId) =>
        fetchChain(
          chainId,
          SPARK_VAULTS_V2.filter((v) => v.chainId === chainId),
        ),
      ),
      fetchSusdcApr(),
    ])

    const out: Record<string, number> = {}
    for (const r of settled) {
      if (r.status === 'fulfilled') Object.assign(out, r.value)
    }
    if (Object.keys(out).length === 0) {
      throw new Error('Spark: no chain returned a readable rate')
    }
    return out
  },
}

/** @deprecated Use {@link sparkSavingsFetcher} — it covers V1 too. */
export const sparkVaultsV2Fetcher = sparkSavingsFetcher
